import React, { useCallback, useEffect, useMemo, useState } from "react";
import Empty from "antd/es/empty";
import { IconButton, Menu, MenuItem } from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { Button, Modal, Table } from "../index";
import { DOCUMENT_ACTION } from "../../redux/action";
import { dateFormatter, Toaster } from "../../utils/common";
import { openDocumentInNewTab } from "../../utils/openDocumentInNewTab";
import {
  getSignatureStatusMeta,
  parseDocuSignConsentError,
} from "../../utils/docusignSignature";
import DocuSignConnectionStatus from "../DocuSignConnectionStatus/DocuSignConnectionStatus";
import SignWellEmbedFrame from "../SignWellEmbedFrame/SignWellEmbedFrame";
import "../DocuSignSendModal/docusign-send-modal.scss";
import "./docusign-signature-panel.scss";

function normalizeEnvelopeRows(response) {
  const rows = response?.data?.envelopes ?? response?.data ?? response?.envelopes;
  return Array.isArray(rows) ? rows : [];
}

function signedFileName(documentName) {
  const base = String(documentName || "document")
    .replace(/\.[^.]+$/, "")
    .trim();
  return `${base || "document"}_signed.pdf`;
}

function formatDateOnly(value) {
  if (!value) return "—";
  return dateFormatter(value) || "—";
}

function getSignerEmails(item) {
  const recipients = Array.isArray(item?.recipients) ? item.recipients : [];
  const signerEmails = recipients
    .filter((recipient) => {
      const role = String(recipient?.role || "signer")
        .trim()
        .toLowerCase();
      return role === "signer" || !role;
    })
    .map((recipient) => String(recipient?.email || "").trim())
    .filter(Boolean);

  if (signerEmails.length) {
    return [...new Set(signerEmails)];
  }

  const fallback =
    item?.signer_email ||
    item?.primary_signer_email ||
    item?.recipient_email ||
    "";
  return fallback ? [String(fallback).trim()] : [];
}

function EnvelopeActionsMenu({
  item,
  envelopeId,
  canManage,
  canSign,
  isCompleted,
  isBusy,
  onViewSigned,
  onSign,
  onResend,
  onVoid,
}) {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const hasOptions = isCompleted || canManage || canSign;

  if (!hasOptions) return "—";

  return (
    <>
      <IconButton
        size="small"
        aria-label="Actions"
        onClick={(event) => setAnchorEl(event.currentTarget)}
        disabled={isBusy}
      >
        <MoreVertIcon fontSize="small" />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        {isCompleted ? (
          <MenuItem
            disabled={isBusy}
            onClick={() => {
              setAnchorEl(null);
              onViewSigned(item);
            }}
          >
            {isBusy ? "Opening…" : "View signed"}
          </MenuItem>
        ) : null}
        {canSign ? (
          <MenuItem
            disabled={isBusy}
            onClick={() => {
              setAnchorEl(null);
              onSign(item);
            }}
          >
            Sign
          </MenuItem>
        ) : null}
        {canManage ? (
          <MenuItem
            onClick={() => {
              setAnchorEl(null);
              onResend(envelopeId);
            }}
          >
            Resend
          </MenuItem>
        ) : null}
        {canManage ? (
          <MenuItem
            onClick={() => {
              setAnchorEl(null);
              onVoid(envelopeId);
            }}
          >
            Void
          </MenuItem>
        ) : null}
      </Menu>
    </>
  );
}

export default function DocuSignSignaturePanel({
  clientId,
  documentCategory,
  clientBusinessId = null,
  readOnly = false,
  refreshKey = 0,
  showAdminTools = false,
}) {
  const [loading, setLoading] = useState(false);
  const [envelopes, setEnvelopes] = useState([]);
  const [busyEnvelopeId, setBusyEnvelopeId] = useState(null);
  const [signingUrl, setSigningUrl] = useState("");

  const loadEnvelopes = useCallback(async () => {
    if (!clientId) {
      setEnvelopes([]);
      return;
    }
    setLoading(true);
    try {
      const response = await DOCUMENT_ACTION.getClientSignatureEnvelopes({
        client_id: clientId,
        document_category: documentCategory,
        ...(clientBusinessId ? { client_business_id: clientBusinessId } : {}),
      });
      if (response?.status === 1 || response?.data) {
        setEnvelopes(normalizeEnvelopeRows(response));
        return;
      }
      setEnvelopes([]);
    } catch (error) {
      setEnvelopes([]);
      if (error?.response?.status !== 404) {
        Toaster.error(
          error?.response?.data?.message ||
            "Unable to load signature requests.",
        );
      }
    } finally {
      setLoading(false);
    }
  }, [clientId, documentCategory, clientBusinessId]);

  useEffect(() => {
    loadEnvelopes();
  }, [loadEnvelopes, refreshKey]);

  const resolveSignedDocumentUrl = useCallback(async (envelopeId, currentUrl) => {
    if (currentUrl) return currentUrl;
    if (!envelopeId) return null;

    const detail = await DOCUMENT_ACTION.getSignatureEnvelopeStatus(envelopeId);
    const row = detail?.data || detail;
    return row?.signed_document_url || null;
  }, []);

  const openSignedDocument = useCallback(
    async (item) => {
      const envelopeId = item?.envelope_id || item?.docusign_envelope_id;
      const status = String(item?.status || "").toLowerCase();
      if (!["completed", "signed"].includes(status)) {
        Toaster.warning("Signed form is available after signing is completed.");
        return;
      }

      setBusyEnvelopeId(envelopeId);
      try {
        const signedUrl = await resolveSignedDocumentUrl(
          envelopeId,
          item?.signed_document_url,
        );
        if (!signedUrl) {
          Toaster.error(
            "Signed form is not available yet. Try Refresh in a moment.",
          );
          await loadEnvelopes();
          return;
        }

        const documentName = signedFileName(item?.document_name);
        const response = await DOCUMENT_ACTION.downloadViewFile({
          fileURL: signedUrl,
          document_name: documentName,
          document_type: "application/pdf",
          type: "Preview",
        });

        await openDocumentInNewTab(
          response?.raw,
          "application/pdf",
          documentName,
        );

        setEnvelopes((prev) =>
          prev.map((row) => {
            const id = row?.envelope_id || row?.docusign_envelope_id;
            if (String(id) !== String(envelopeId)) return row;
            return { ...row, signed_document_url: signedUrl };
          }),
        );
      } catch (error) {
        Toaster.error(
          error?.response?.data?.message ||
            error?.message ||
            "Unable to open the signed form.",
        );
      } finally {
        setBusyEnvelopeId(null);
      }
    },
    [loadEnvelopes, resolveSignedDocumentUrl],
  );

  const handleSign = useCallback(async (item) => {
    const envelopeId = item?.envelope_id || item?.docusign_envelope_id;
    if (!envelopeId) return;
    setBusyEnvelopeId(envelopeId);
    try {
      const response = await DOCUMENT_ACTION.getSignatureSigningUrl(envelopeId);
      const url = response?.data?.embedded_signing_url;
      if (response?.status === 1 && url) {
        setSigningUrl(url);
        return;
      }
      Toaster.error(response?.message || "Unable to open the signing window.");
    } catch (error) {
      Toaster.error(
        error?.response?.data?.message ||
          error?.message ||
          "Unable to open the signing window.",
      );
    } finally {
      setBusyEnvelopeId(null);
    }
  }, []);

  const handleResend = useCallback(
    async (envelopeId) => {
      try {
        const response =
          await DOCUMENT_ACTION.resendSignatureEnvelope(envelopeId);
        if (response?.status === 1) {
          Toaster.success(response?.message || "Reminder sent.");
          loadEnvelopes();
          return;
        }
        Toaster.error(response?.message || "Failed to resend envelope.");
      } catch (error) {
        const consentError = parseDocuSignConsentError(error);
        Toaster.error(
          consentError.message || "Failed to resend envelope.",
        );
      }
    },
    [loadEnvelopes],
  );

  const handleVoid = useCallback(
    async (envelopeId) => {
      const reason = window.prompt(
        "Reason for voiding this signature request (optional):",
        "",
      );
      if (reason === null) return;
      try {
        const response = await DOCUMENT_ACTION.voidSignatureEnvelope(
          envelopeId,
          reason,
        );
        if (response?.status === 1) {
          Toaster.success(response?.message || "Envelope voided.");
          loadEnvelopes();
          return;
        }
        Toaster.error(response?.message || "Failed to void envelope.");
      } catch (error) {
        const consentError = parseDocuSignConsentError(error);
        Toaster.error(consentError.message || "Failed to void envelope.");
      }
    },
    [loadEnvelopes],
  );

  const tableData = useMemo(
    () =>
      envelopes.map((item) => {
        const recipients = Array.isArray(item?.recipients)
          ? item.recipients
          : [];
        const statusMeta = getSignatureStatusMeta(item?.status, recipients);
        const envelopeId = item?.envelope_id || item?.docusign_envelope_id;
        const statusKey = String(item?.status || "").toLowerCase();
        const isCompleted = ["completed", "signed"].includes(statusKey);
        const canManage =
          !readOnly &&
          envelopeId &&
          !["completed", "signed", "voided", "declined", "expired"].includes(
            statusKey,
          );
        const canSign =
          Boolean(envelopeId) &&
          !["completed", "signed", "voided", "declined", "expired"].includes(
            statusKey,
          );
        const isBusy = String(busyEnvelopeId) === String(envelopeId);
        const signerEmails = getSignerEmails(item);

        const documentLabel = item?.document_name || "—";

        return {
          document_name:
            documentLabel !== "—" && isCompleted ? (
              <button
                type="button"
                className="docusign-signature-panel__doc-link"
                disabled={isBusy}
                onClick={() => openSignedDocument(item)}
              >
                {documentLabel}
              </button>
            ) : (
              documentLabel
            ),
          signer_email:
            signerEmails.length > 0 ? (
              <div className="docusign-signature-panel__signers">
                {signerEmails.map((email) => (
                  <div key={email}>{email}</div>
                ))}
              </div>
            ) : (
              "—"
            ),
          status: (
            <span className={`badge ${statusMeta.className}`}>
              {statusMeta.label}
            </span>
          ),
          sent_at: formatDateOnly(item?.sent_at || item?.created_at),
          completed_at: isCompleted ? formatDateOnly(item?.completed_at) : "—",
          actions: isCompleted || canManage || canSign ? (
            <EnvelopeActionsMenu
              item={item}
              envelopeId={envelopeId}
              canManage={canManage}
              canSign={canSign}
              isCompleted={isCompleted}
              isBusy={isBusy}
              onViewSigned={openSignedDocument}
              onSign={handleSign}
              onResend={handleResend}
              onVoid={handleVoid}
            />
          ) : (
            <span className="docusign-signature-panel__pending">
              View after completion
            </span>
          ),
        };
      }),
    [
      envelopes,
      readOnly,
      busyEnvelopeId,
      handleResend,
      handleVoid,
      handleSign,
      openSignedDocument,
    ],
  );

  if (!clientId) {
    return (
      <div className="docusign-signature-panel">
        <Empty description="Select a client to view signature requests." />
      </div>
    );
  }

  return (
    <div className="docusign-signature-panel">
      {showAdminTools ? <DocuSignConnectionStatus /> : null}
      <div className="docusign-signature-panel__head">
        <div>
          <h3>Signature Requests</h3>
          <p>
            All signature requests are listed here. Viewing the signed file is
            available only after signing is completed.
          </p>
        </div>
        <Button
          type="button"
          variant="outline"
          text={loading ? "Syncing…" : "Refresh"}
          disable={loading}
          handleClick={loadEnvelopes}
        />
      </div>

      {loading && envelopes.length === 0 ? (
        <p className="docusign-signature-panel__loading">Loading…</p>
      ) : envelopes.length === 0 ? (
        <Empty description="No signature requests yet. Use Send for Signature on a PDF, DOC, or DOCX file." />
      ) : (
        <Table
          title=""
          header={[
            { id: "document_name", label: "Document" },
            { id: "signer_email", label: "Signer" },
            { id: "status", label: "Status" },
            { id: "sent_at", label: "Sent" },
            { id: "completed_at", label: "Completed" },
            { id: "actions", label: "Actions" },
          ]}
          data={tableData}
          aligncss="dashboard-table"
          variant="small"
        />
      )}
      <Modal
        title={null}
        open={Boolean(signingUrl)}
        handleClose={() => {
          setSigningUrl("");
          loadEnvelopes();
        }}
        maxWidth
        className="docusign-send-modal docusign-send-modal--signing"
      >
        <div className="docusign-send-modal__shell">
          <div className="docusign-send-modal__header">
            <h3 className="docusign-send-modal__title">Sign document</h3>
          </div>
          {signingUrl ? (
            <SignWellEmbedFrame
              url={signingUrl}
              containerId="signwell-panel-frame"
              onCompleted={() => {
                Toaster.success("Document signed.");
                setSigningUrl("");
                loadEnvelopes();
              }}
              onClosed={() => {
                setSigningUrl("");
                loadEnvelopes();
              }}
              onDeclined={() => {
                Toaster.warning("Signature was declined.");
                setSigningUrl("");
                loadEnvelopes();
              }}
              onError={() => {
                Toaster.error("Unable to open the SignWell signing window.");
              }}
            />
          ) : null}
        </div>
      </Modal>
    </div>
  );
}
